import { ref, shallowRef } from 'vue'
import type { Badge } from '@/types/gamification'

// Singleton state for badge notifications
const showDialog = ref(false)
const currentBadge = shallowRef<Badge | null>(null)
const badgeQueue: Badge[] = []

/**
 * Composable for managing badge unlock notifications
 * Uses a queue to handle multiple badge unlocks
 */
export function useBadgeNotification() {
  /**
   * Show a badge unlock notification
   * If a badge is already being shown, queue it
   */
  function showBadgeUnlock(badge: Badge) {
    if (showDialog.value) {
      // Queue the badge if dialog is already open
      badgeQueue.push(badge)
    } else {
      currentBadge.value = badge
      showDialog.value = true
    }
  }

  /**
   * Close the current badge notification
   * Shows next queued badge if any
   */
  function closeBadgeNotification() {
    showDialog.value = false
    currentBadge.value = null

    // Check if there are queued badges
    if (badgeQueue.length > 0) {
      const nextBadge = badgeQueue.shift()!
      // Small delay before showing next badge
      setTimeout(() => {
        currentBadge.value = nextBadge
        showDialog.value = true
      }, 300)
    }
  }

  return {
    showDialog,
    currentBadge,
    showBadgeUnlock,
    closeBadgeNotification,
  }
}

// Global instance for use outside Vue components
let globalNotificationCallback: ((badge: Badge) => void) | null = null

export function setBadgeNotificationCallback(callback: (badge: Badge) => void) {
  globalNotificationCallback = callback
}

export function triggerBadgeNotification(badge: Badge) {
  if (globalNotificationCallback) {
    globalNotificationCallback(badge)
  } else {
    console.info('[Badge] Notification callback not set, badge:', badge.nameFr)
  }
}
